import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Share2, Download, Loader2 } from "lucide-react";
import { getVisitorId } from "../utils/analytics";
import sfx from "../utils/sound";

const CARD_W = 720;
const CARD_H = 900;

// Draw a rounded rect helper — canvas doesn't have one in older Safari.
const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawShareCard = async ({ ctx, avatar, name, rank, value, sortLabel, qrDataUrl }) => {
  // Background — cream with a soft sprinkle of dots.
  ctx.fillStyle = "#FFF9E6";
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  ctx.fillStyle = "rgba(255, 180, 0, 0.10)";
  for (let i = 0; i < 60; i++) {
    const x = Math.random() * CARD_W;
    const y = Math.random() * CARD_H;
    ctx.beginPath();
    ctx.arc(x, y, 6 + Math.random() * 10, 0, Math.PI * 2);
    ctx.fill();
  }
  // Border
  ctx.lineWidth = 10;
  ctx.strokeStyle = "#2b2b2b";
  roundRect(ctx, 20, 20, CARD_W - 40, CARD_H - 40, 32);
  ctx.stroke();

  // Header pill
  ctx.fillStyle = "#FF5A5F";
  roundRect(ctx, 60, 60, CARD_W - 120, 96, 24);
  ctx.fill();
  ctx.lineWidth = 6;
  ctx.strokeStyle = "#2b2b2b";
  ctx.stroke();
  ctx.fillStyle = "#fff";
  ctx.font = "700 44px Fredoka, Nunito, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("67Cafe — Cafe Card", CARD_W / 2, 60 + 48);

  // Avatar circle
  const avatarCX = CARD_W / 2;
  const avatarCY = 300;
  ctx.beginPath();
  ctx.arc(avatarCX, avatarCY, 90, 0, Math.PI * 2);
  ctx.fillStyle = "#EAF6FF";
  ctx.fill();
  ctx.lineWidth = 8;
  ctx.strokeStyle = "#2b2b2b";
  ctx.stroke();
  ctx.font = "120px 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif";
  ctx.fillText(avatar || "🐻", avatarCX, avatarCY + 6);

  // Name (first 3 letters, uppercase to fit the vibe)
  ctx.fillStyle = "#2b2b2b";
  ctx.font = "700 72px Fredoka, Nunito, sans-serif";
  ctx.fillText((name || "???").toUpperCase(), CARD_W / 2, 460);

  // Rank pill
  const rankText = `Rank #${rank}`;
  ctx.font = "700 44px Fredoka, Nunito, sans-serif";
  const rankW = ctx.measureText(rankText).width + 96;
  const rankX = (CARD_W - rankW) / 2;
  const rankY = 510;
  ctx.fillStyle = "#FFB400";
  roundRect(ctx, rankX, rankY, rankW, 76, 32);
  ctx.fill();
  ctx.lineWidth = 6;
  ctx.strokeStyle = "#2b2b2b";
  ctx.stroke();
  ctx.fillStyle = "#2b2b2b";
  ctx.fillText(rankText, CARD_W / 2, rankY + 44);

  // Sub-stat (playtime / coins / plays). Positioned just below the rank pill
  // with enough breathing room to clear the QR panel drawn below.
  ctx.fillStyle = "#484848";
  ctx.font = "500 30px Nunito, sans-serif";
  ctx.fillText(`${sortLabel}: ${value}`, CARD_W / 2, 605);

  // QR code + call to action. The card has a 10px border stroke covering
  // y875-885, so we place the QR panel higher and the caption WELL above
  // that inner border edge to keep both perfectly readable.
  const qrSize = 170;
  const qrX = (CARD_W - qrSize) / 2;
  const qrY = 640;
  if (qrDataUrl) {
    const img = new Image();
    img.src = qrDataUrl;
    await new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });
    ctx.fillStyle = "#fff";
    roundRect(ctx, qrX - 12, qrY - 12, qrSize + 24, qrSize + 24, 16);
    ctx.fill();
    ctx.lineWidth = 6;
    ctx.strokeStyle = "#2b2b2b";
    ctx.stroke();
    ctx.drawImage(img, qrX, qrY, qrSize, qrSize);
  }
  ctx.fillStyle = "#484848";
  ctx.font = "500 22px Nunito, sans-serif";
  // textBaseline is 'middle' from earlier in this function, so we position
  // the glyph CENTRE, not the baseline. Card inner-border top edge is y875,
  // so keep the glyph centre <= 855 (glyph half-height ~13px = clear at y842-868).
  ctx.fillText("Scan to play — 67cafe.com", CARD_W / 2, 850);
};

// Turn a data URL into a File so Web Share API can share it.
const dataUrlToFile = async (dataUrl, filename) => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: "image/png" });
};

const CafeCardShare = ({ open, onClose, row, sortLabel, sortValue }) => {
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [sharingErr, setSharingErr] = useState("");

  useEffect(() => {
    if (!open || !row) return;
    let cancelled = false;
    (async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = CARD_W;
      canvas.height = CARD_H;
      const ctx = canvas.getContext("2d");
      const shareUrl = `https://67cafe.com/?ref=${encodeURIComponent(row.visitor_id || getVisitorId())}`;
      let qrDataUrl = "";
      try {
        qrDataUrl = await QRCode.toDataURL(shareUrl, {
          margin: 1,
          width: 400,
          color: { dark: "#2b2b2b", light: "#ffffff" },
        });
      } catch { /* QR is optional */ }
      await drawShareCard({
        ctx,
        avatar: row.avatar || "🐻",
        name: row.name || "???",
        rank: row.rank,
        value: sortValue,
        sortLabel,
        qrDataUrl,
      });
      if (!cancelled) setImgUrl(canvas.toDataURL("image/png"));
    })();
    return () => { cancelled = true; };
  }, [open, row, sortLabel, sortValue]);

  if (!open) return null;

  const share = async () => {
    if (!imgUrl || busy) return;
    setBusy(true);
    setSharingErr("");
    try {
      const file = await dataUrlToFile(imgUrl, "67cafe-card.png");
      const shareData = {
        title: "My 67Cafe rank!",
        text: `I'm rank #${row.rank} at 67Cafe — come play with me!`,
        url: `https://67cafe.com/?ref=${encodeURIComponent(row.visitor_id || getVisitorId())}`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        sfx.pop();
      } else if (navigator.share) {
        // Some browsers can share text/url but not files — fall back.
        await navigator.share({ title: shareData.title, text: shareData.text, url: shareData.url });
        sfx.pop();
      } else {
        // Desktop fallback → download the image.
        const a = document.createElement("a");
        a.href = imgUrl;
        a.download = "67cafe-card.png";
        document.body.appendChild(a);
        a.click();
        a.remove();
        sfx.pop();
      }
    } catch (e) {
      if (e && e.name !== "AbortError") setSharingErr("Could not share. Try Download instead.");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = "67cafe-card.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    sfx.pop();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      data-testid="cafe-card-modal"
      onClick={onClose}
    >
      <div
        className="chunky-card p-4 max-w-md w-full"
        style={{ background: "#fff" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl">Your Cafe Card</h2>
          <button
            onClick={onClose}
            className="btn-3d btn-sm btn-ghost"
            data-testid="cafe-card-close"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="rounded-2xl overflow-hidden mb-3" style={{ border: "3px solid #2b2b2b", background: "#FFF9E6" }}>
          {imgUrl ? (
            <img
              src={imgUrl}
              alt="Your 67Cafe rank card"
              data-testid="cafe-card-preview"
              style={{ width: "100%", display: "block" }}
            />
          ) : (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="animate-spin" size={32} />
            </div>
          )}
        </div>
        <canvas ref={canvasRef} style={{ display: "none" }} data-testid="cafe-card-canvas" />
        {sharingErr && (
          <div className="text-sm mb-2" style={{ color: "#c0392b" }} data-testid="cafe-card-error">
            {sharingErr}
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={share}
            disabled={busy || !imgUrl}
            className="btn-3d flex-1"
            data-testid="cafe-card-share-btn"
            style={{ background: "#54A0FF" }}
          >
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Share2 size={18} strokeWidth={3} />}
            Share
          </button>
          <button
            onClick={download}
            disabled={!imgUrl}
            className="btn-3d flex-1"
            data-testid="cafe-card-download-btn"
            style={{ background: "#FFB400" }}
          >
            <Download size={18} strokeWidth={3} />
            Download
          </button>
        </div>
        <p className="text-xs text-center mt-3" style={{ color: "#484848" }}>
          Sharing sends only your first-3 letters, avatar and rank — never your full name.
        </p>
      </div>
    </div>
  );
};

export default CafeCardShare;
